import Vue from 'vue';
import { Model } from '@viddsee/server-api-client';
import { VideoPlayerActionContext } from '../../types';
import { createWebcall, FilmReviewServiceWebcall } from './filmReviewService/webcall';
import { FilmReviewService, FilmReviewServiceData, FilmSegment, Zone } from './filmReviewService/types';
import { createFilmReviewServiceAnsweringMethods } from './filmReviewService/answeringController';
import { createFilmReviewServicePresenterMethods } from './filmReviewService/presenterController';
import { Questions } from './filmReviewService/class/Questions';
import { isMobileView } from './filmReviewService/helpers';

type Ctx = VideoPlayerActionContext;

const getCurrentVideo = (context: Ctx): Model.Video.Info | null => context.rootState.video.video || null;

export const FilmReviewServiceName = Symbol('FilmReviewService');

const NoService: FilmReviewService = {
  name: FilmReviewServiceName,
  data: {
    segment: null,
    questions: null,
    methods: {
      ui: createFilmReviewServicePresenterMethods(null),
      answers: createFilmReviewServiceAnsweringMethods(null, null, null),
    },
  },
};

const isBetween = (start: number, end: number) => (position: number): boolean => start <= position && position < end;

const getSegmentComputer = (video: Model.Video.Info): ((position: number, duration: number) => FilmSegment | null) => {
  const actualCreditStartTime = video.creditsStart || video.durationSeconds - 10;
  const adjustedCreditStartTime = actualCreditStartTime - 1;

  /**
   * Requirement:
   * - 1st trigger - when video play midway through
   * - 2nd trigger - when video play 3/4 mark
   */
  const halfTime = adjustedCreditStartTime * 0.5;
  const thirQuarterTime = adjustedCreditStartTime * 0.75;
  const hintShowDuration = 10;

  const segments: FilmSegment[] = [
    {
      name: 'completed zone',
      zone: Zone.Completed,
      start: video.durationSeconds - 1,
      end: Infinity,
    },
    {
      name: 'credit zone',
      zone: Zone.Credit,
      start: adjustedCreditStartTime,
      end: video.durationSeconds,
    },
    {
      name: 'half zone',
      zone: Zone.Hint,
      start: halfTime,
      end: halfTime + hintShowDuration,
    },
    {
      name: 'third quarter zone',
      zone: Zone.Hint,
      start: thirQuarterTime,
      end: thirQuarterTime + hintShowDuration,
    },
  ];

  interface FilmSegmentEvaluationWrapper {
    segment: FilmSegment;
    evaluator: ReturnType<typeof isBetween>;
  }

  const filmSegmentEvaluationWrappers: FilmSegmentEvaluationWrapper[] = segments.map((segment) => ({
    segment,
    evaluator: isBetween(segment.start, segment.end),
  }));

  const creditRelatedFilmSegmentEvaluationWrappers = filmSegmentEvaluationWrappers.filter(({ segment }) => segment.zone !== Zone.Hint);

  if (adjustedCreditStartTime < 55) {
    return (position: number): FilmSegment | null => (
      creditRelatedFilmSegmentEvaluationWrappers.find(({ evaluator }) => evaluator(position))?.segment || null
    );
  }

  return (position: number): FilmSegment | null => (
    filmSegmentEvaluationWrappers.find(({ evaluator }) => evaluator(position))?.segment || null
  );
};

interface ServiceVm {
  data: FilmReviewServiceData;
  destroy: () => void;
}

const createServiceVm = (context: Ctx, webcall: FilmReviewServiceWebcall): ServiceVm => {
  const vm = new Vue({
    data(): FilmReviewServiceData {
      return {
        segment: null,
        questions: null,
        methods: {
          ui: createFilmReviewServicePresenterMethods(context),
          answers: createFilmReviewServiceAnsweringMethods(context, webcall, this as any),
        },
      };
    },

    watch: {
      segment(segment: FilmSegment | null): void {
        switch (segment?.zone) {
          case Zone.Hint: {
            this.methods.ui.controlInterface({
              hint: true,
              questions: null, // Change affect questions overlay
            });
            return;
          }

          case Zone.Completed: {
            this.methods.ui.controlInterface({
              hint: true,
              questions: null, // Keep current state
            });
            return;
          }

          case Zone.Credit: {
            this.methods.ui.controlInterface({
              hint: true,
              questions: isMobileView() ? null : true,
            });
            return;
          }

          case Zone.Void:
          default: {
            this.methods.ui.controlInterface({
              hint: false,
              questions: false,
            });
          }
        }
      },
    },

    beforeDestroy(): void {
      this.methods.ui.controlInterface({
        hint: false,
        questions: false,
      });
    },
  });

  return {
    data: {
      get questions(): FilmReviewServiceData['questions'] {
        return vm.questions;
      },
      set questions(questions) {
        vm.questions = questions;
      },
      get segment(): FilmReviewServiceData['segment'] {
        return vm.segment;
      },
      set segment(segment) {
        vm.segment = segment;
      },

      methods: vm.methods,
    },

    destroy(): void {
      vm.$destroy();
    },
  };
};

export const registerFilmReviewService = (context: Ctx): FilmReviewService => {
  const video = getCurrentVideo(context);
  const player = context.getters.player;

  if (!video || !player) return NoService;

  const webcall = createWebcall({ videoId: video.id });

  const serviceVm = createServiceVm(context, webcall);

  const getSegmentComputerForVideo = getSegmentComputer(video);
  const playerTimeHandler = (param: jwplayer.TimeParam): void => {
    serviceVm.data.segment = getSegmentComputerForVideo(param.position, param.duration);
  };

  player.on('time', playerTimeHandler);
  const destroyService = (): void => {
    player.off('time', playerTimeHandler);
    serviceVm.destroy();
  };

  (async(): Promise<void> => {
    const listPromise = webcall.getQuestions();
    try {
      const { hasReviewed } = await webcall.checkReviewStatus();

      if (hasReviewed) {
        destroyService();
        return;
      }

      const { items: questionInfos } = await listPromise;
      if (questionInfos.length === 0) {
        destroyService();
        return;
      }

      serviceVm.data.questions = new Questions(questionInfos);
    } catch (error) {
      destroyService();
      throw error;
    }
  })();

  return Object.freeze({
    name: FilmReviewServiceName,
    data: serviceVm.data,
    destroy: destroyService,
  });
};
